import type {
  AvailabilityProvider
} from './visitorCalendarProviders'

// Cross-pageload persistence for the visitor-availability overlay, so a
// refresh — or a second scheduling link from the same host — reuses the grant
// the visitor already gave instead of re-opening a consent popup.
//
// It lives in localStorage (shared across every link on this origin, which is
// the point) rather than in the provider SDKs' own caches: Google's GIS token
// client has no silent path that survives a reload without a popup, so a
// symmetric store is the only shape that behaves the same for both providers.
// The token is a read-only calendar grant that expires within the hour and
// never leaves the browser; nothing is sent to our backend, and the lead POST
// fires only on the click that granted it, never on a restore.
//
// The consequence of localStorage over sessionStorage, chosen deliberately: on
// a SHARED or KIOSK browser the next visitor's booking page restores the
// previous visitor's identity and busy pattern, for as long as the token
// lives. sessionStorage would confine that to one tab — but the case being
// bought here is exactly the cross-tab one (a second link opened from an
// email), which sessionStorage cannot serve. Mitigations: the grant is
// read-only and hour-bounded, the connected strip names the restored address,
// and "Disconnect" clears the store.

const STORAGE_KEY = 'catch.scheduling.visitorAvailability'
// Don't restore a token about to expire mid-fetch — a 401 halfway through the
// free/busy read reads to the visitor as a random failure.
const EXPIRY_SKEW_MS = 60_000

export interface VisitorAvailabilitySession {
  provider: AvailabilityProvider
  email: string
  name: string | null
  accessToken: string
  /** Epoch ms. */
  expiresAt: number
  // The filter rides along with the grant: restoring the connection but
  // resetting the toggle would silently re-hide slots a visitor had chosen
  // to see, on every reload.
  hideBusy: boolean
}

function isSession(value: unknown): value is VisitorAvailabilitySession {
  if (typeof value !== 'object' || value === null) return false
  const s = value as Partial<Record<keyof VisitorAvailabilitySession, unknown>>
  return (
    (s.provider === 'google' || s.provider === 'microsoft')
    && typeof s.email === 'string'
    && (typeof s.name === 'string' || s.name === null)
    && typeof s.accessToken === 'string'
    && typeof s.expiresAt === 'number'
    && typeof s.hideBusy === 'boolean'
  )
}

// Storage throws outright in Safari private mode and when a visitor has
// blocked site data — the overlay must degrade to per-pageload, not crash the
// booking page.
export function loadVisitorSession(now: number = Date.now()): VisitorAvailabilitySession | null {
  let parsed: unknown = null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    parsed = JSON.parse(raw)
  } catch {
    // Unreadable storage, or a corrupt entry: both mean no usable session.
  }
  // A stale shape (or another tab's junk) must not restore a half-session the
  // hook would then hand to fetchBusy as an undefined token.
  if (!isSession(parsed) || parsed.expiresAt - EXPIRY_SKEW_MS <= now) {
    clearVisitorSession()
    return null
  }
  return parsed
}

export function saveVisitorSession(session: VisitorAvailabilitySession): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Persistence is an optimisation; the overlay still works for this pageload.
  }
}

export function clearVisitorSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do — a store we can't write is a store we can't leak from.
  }
}
